import React from 'react';

const whyChooseFeatures = [
  {
    id: 'custom-experience',
    title: 'Customized Shopping Experience',
    desc: 'We craft tailored digital shopping journeys that resonate with your target buyers. By integrating AI-driven product recommendations, personalized search filtering, and bespoke UI interactions, we make every shopper feel valued and eager to return.',
    animClass: 'anim-pulse-glow',
    icon: (
      <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
        <line x1="3" y1="6" x2="21" y2="6" />
        <path d="M16 10a4 4 0 0 1-8 0" />
        <path d="m12 15 1 2 2 1-2 1-1 2-1-2-2-1 2-1z" fill="#c40a0a" stroke="#c40a0a" />
      </svg>
    )
  },
  {
    id: 'seo-optimized',
    title: 'SEO Optimized Website',
    desc: 'Our e-commerce stores are architected from the ground up with clean semantic code, automated schema markups, optimized category hierarchies, and canonical URL handling to guarantee maximum organic visibility on Google search results.',
    animClass: 'anim-radar-scan',
    icon: (
      <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
        <polyline points="8 11 11 8 14 11" stroke="#c40a0a" />
        <line x1="11" y1="8" x2="11" y2="14" stroke="#c40a0a" />
      </svg>
    )
  },
  {
    id: 'loading-speeds',
    title: 'Quick Loading Speeds',
    desc: 'Speed directly drives revenue. We implement advanced server-side caching, headless rendering, next-gen WebP compression, and edge CDN integration to achieve sub-second page load times that keep bounce rates at an all-time low.',
    animClass: 'anim-speed-dash',
    icon: (
      <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
        <line x1="2" y1="2" x2="5" y2="5" stroke="#c40a0a" />
        <line x1="19" y1="19" x2="22" y2="22" stroke="#c40a0a" />
      </svg>
    )
  },
  {
    id: 'user-centric',
    title: 'User Centric Design',
    desc: 'Every button, menu, and product grid is engineered around buyer psychology. We design intuitive mobile-first navigation systems, frictionless 1-click checkout pathways, and clear product discovery zones that eliminate shopping hesitation.',
    animClass: 'anim-device-float',
    icon: (
      <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="5" y="2" width="14" height="20" rx="3" />
        <line x1="12" y1="18" x2="12.01" y2="18" stroke="#c40a0a" strokeWidth="3" />
        <circle cx="12" cy="9" r="3" stroke="#c40a0a" />
        <path d="m9 9 6 0" />
      </svg>
    )
  },
  {
    id: 'smart-inventory',
    title: 'Smart Inventory Management',
    desc: 'Effortlessly manage complex multi-category catalogs. We integrate automated stock synchronization across channels, real-time supplier ERP feeds, backorder management, and centralized warehouse logistics to prevent overselling.',
    animClass: 'anim-sync-rotate',
    icon: (
      <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
        <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
        <line x1="12" y1="22.08" x2="12" y2="12" stroke="#c40a0a" />
      </svg>
    )
  },
  {
    id: 'more-conversions',
    title: 'More Conversions',
    desc: 'Turn casual site visitors into high-paying repeat customers. Through behavioral A/B testing, strategic trust badges, dynamic exit-intent triggers, and streamlined checkout funnels, our stores consistently deliver industry-leading sales uplifts.',
    animClass: 'anim-growth-lift',
    icon: (
      <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" stroke="#c40a0a" strokeWidth="2.5" />
        <polyline points="17 6 23 6 23 12" stroke="#c40a0a" strokeWidth="2.5" />
        <circle cx="6" cy="18" r="3" />
        <circle cx="18" cy="6" r="3" />
      </svg>
    )
  }
];

function WhyChooseEcommerce() {
  const handleCtaClick = (e) => {
    e.preventDefault();
    const formEl = document.getElementById('ecommerce-hero-form') || document.querySelector('.contact-section');
    if (formEl) {
      formEl.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.location.href = '/contact/';
    }
  };

  return (
    <section className="why-choose-ecom-section">
      <div className="container">
        {/* Section Header */}
        <div className="why-choose-ecom-header">
          <div className="why-choose-ecom-badge-row">
            <span className="why-choose-ecom-badge">Competitive Advantage</span>
            <div className="why-choose-ecom-badge-line"></div>
          </div>

          <h2 className="why-choose-ecom-title">
            Why Choose Our <span className="text-red">E-commerce Web Development</span> Services?
          </h2>

          <p className="why-choose-ecom-subtitle">
            We merge cutting-edge technology with conversion science to engineer digital storefronts that dominate marketplaces and maximize profit margins.
          </p>
        </div>

        {/* 6 Interactive Cards Grid with Live Animated Icons */}
        <div className="why-choose-ecom-grid">
          {whyChooseFeatures.map((item, index) => (
            <div key={item.id} className="why-choose-ecom-card" onClick={handleCtaClick}>
              {/* Card Top: Live Animated Icon Box & Number */}
              <div className="why-choose-card-top">
                <div className={`why-choose-icon-box ${item.animClass}`}>
                  {item.icon}
                </div>
                <span className="why-choose-card-num">0{index + 1}</span>
              </div>

              {/* Title with red accent underline */}
              <h3 className="why-choose-card-title">{item.title}</h3>

              {/* Description */}
              <p className="why-choose-card-desc">{item.desc}</p>

              {/* Action Corner Indicator */}
              <div className="why-choose-card-footer">
                <span className="why-choose-learn-more">Learn more</span>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="why-choose-arrow">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default WhyChooseEcommerce;
